import { useCallback, useMemo, useState } from "react";

import { NavigateFunction, useLocation, useNavigate } from "react-router-dom";

import { useLocationHash } from "./useLocationHash";
import { CustomNavigationFunction } from "../state/AppContext";

export interface TabsValue {
  value: string;
  onValueChange: (value: string) => void;
}

export const useTabsHashValue = (
  defaultValue: string,
  customNavigate?: NavigateFunction | CustomNavigationFunction
) => {
  const hash = useLocationHash();
  const _navigate = useNavigate();

  const navigate = customNavigate ?? _navigate;
  const value = hash || defaultValue;

  const onValueChange = useCallback(
    (nextValue: string) => navigate(`#${nextValue}`, { replace: true }),
    [navigate]
  );

  return useMemo(
    () => ({
      value,
      onValueChange,
    }),
    [onValueChange, value]
  );
};

export const useTabsStateValue = (defaultValue: string) => {
  const [value, setValue] = useState(defaultValue);

  const onValueChange = useCallback(
    (nextValue: string) => setValue(nextValue),
    []
  );

  return useMemo(
    () => ({
      value,
      onValueChange,
    }),
    [onValueChange, value]
  );
};

export const useTabsSearchParamsValue = (
  key: string,
  defaultValue: string,
  customNavigate?: NavigateFunction | CustomNavigationFunction
) => {
  const _navigate = useNavigate();

  const navigate = customNavigate ?? _navigate;
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const value = searchParams.get(key) || defaultValue;

  const onValueChange = useCallback(
    (nextValue: string) => {
      const newSearchParams = new URLSearchParams();
      newSearchParams.set(key, nextValue);
      navigate(
        {
          search: newSearchParams.toString(),
        },
        { replace: true }
      );
    },
    [key, navigate]
  );

  return useMemo(
    () => ({
      value,
      onValueChange,
    }),
    [onValueChange, value]
  );
};
